import { FC, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';

import SectionDivider from 'components/pages/Planner/SectionDivider';

import { Store } from 'components/common/types/Store.types';
import { CodeNameModel } from 'components/common/types';
import { calculateSecondaryPanelPosition } from 'components/pages/Planner/PlannerLineActions/calculateSecondaryPanelPosition';
import { isValueRequired } from '../../utils/isValueRequired';
import { isPanelConfirmDisabled } from '../../utils/isPanelConfirmDisabled';
import { permissionOptions } from '../../consts';
import SelectionPermissionDropdown from '../SelectionPermissionDropdown/SelectionPermissionDropdown';
import { ProductPoiProps } from './ProductPoi.types';
import { PoiSelectionContent } from './PoiSelectionContent';
import { useProductPoiPanel } from './useProductPoiPanel';
import { ProductSecondaryPanel } from '../ProductSecondaryPanel/ProductSecondaryPanel';

const SECONDARY_PANEL_WIDTH = 400;

export const ProductPoiPanel: FC<ProductPoiProps> = ({ onCancel, isReadOnly = false }) => {
  const productPoi = useSelector((state: Store) => state.productManagement.newProduct?.poi);
  const selectedPOIs = productPoi?.value ?? [];

  const defaultSelectedPermission =
    permissionOptions.find(({ code }) => code === productPoi?.selectionPermission) ?? permissionOptions[0];

  const [selectedPermission, setSelectedPermission] = useState<CodeNameModel>(defaultSelectedPermission);

  const {
    localSelectedPOIs,
    selectedProvider,
    navigationStack,
    transition,
    currentScreen,
    isMainLevel,
    dataProviders,
    searchTerm,
    currentPoiOptions,
    currentFocusedItemIndex,
    getSelectedPOIIncludeAndRadius,
    isItemSelected,
    onChangeRadius,
    onIncludeExcludeChange,
    resetCurrentFocusedItemIndex,
    getSelectedPOIIncludedExcludedChildrenCounter,
    handleSearchChange,
    handleProviderSelect,
    navigateForward,
    onTransitionComplete,
    onSubmit,
    handleCancel,
    isChange,
    productPoi: currentProductPoiField,
    customPoiCategories,
    onFileChange,
    onDeleteCustomCategory,
    hasReachedFileUploadLimit,
    isInsideCustomCategory,
  } = useProductPoiPanel({ selectedPOIs, selectedPermission, productPoi });

  const isDisabled = useMemo(
    () =>
      isReadOnly ||
      isPanelConfirmDisabled({
        field: currentProductPoiField?.selectionPermission
          ? { selectionPermission: currentProductPoiField.selectionPermission, value: currentProductPoiField.value }
          : undefined,
        selectedPermission,
        newFieldValue: localSelectedPOIs,
      }),
    [currentProductPoiField, selectedPermission, localSelectedPOIs, isReadOnly],
  );

  const title = currentScreen ? `Select in ${currentScreen.title}` : 'Select POI';

  return (
    <ProductSecondaryPanel
      title={title}
      cancelButtonLabel={isMainLevel ? 'Cancel' : 'Back'}
      isChange={isChange}
      isDisabled={isDisabled}
      customStyles={calculateSecondaryPanelPosition({ width: SECONDARY_PANEL_WIDTH })}
      onSubmit={onSubmit}
      onCancel={() => handleCancel(onCancel)}
      showBackButton={!isMainLevel}
      isSubsection={false}
      isMainLevel={isMainLevel}
    >
      <SelectionPermissionDropdown
        selectedPermission={selectedPermission}
        setSelectedPermission={setSelectedPermission}
        permissionOptions={permissionOptions}
      />
      {isValueRequired(selectedPermission.code) && (
        <>
          <div className="my-4">
            <SectionDivider />
          </div>
          <PoiSelectionContent
            isMainLevel={isMainLevel}
            navigationStack={navigationStack}
            selectedProvider={selectedProvider}
            dataProviders={dataProviders}
            searchTerm={searchTerm}
            currentPoiOptions={currentPoiOptions}
            currentFocusedItemIndex={currentFocusedItemIndex}
            getSelectedPOIIncludeAndRadius={getSelectedPOIIncludeAndRadius}
            getSelectedPOIIncludedExcludedChildrenCounter={getSelectedPOIIncludedExcludedChildrenCounter}
            isItemSelected={isItemSelected}
            onChangeRadius={onChangeRadius}
            onIncludeExcludeChange={onIncludeExcludeChange}
            resetCurrentFocusedItemIndex={resetCurrentFocusedItemIndex}
            navigateForward={navigateForward}
            onTransitionComplete={onTransitionComplete}
            transition={transition}
            handleProviderSelect={handleProviderSelect}
            handleSearchChange={handleSearchChange}
            customPoiCategories={customPoiCategories}
            onFileChange={onFileChange}
            onDeleteCustomCategory={onDeleteCustomCategory}
            hasReachedFileUploadLimit={hasReachedFileUploadLimit}
            isInsideCustomCategory={isInsideCustomCategory}
          />
        </>
      )}
    </ProductSecondaryPanel>
  );
};
